import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, AlertCircle, Heart, CheckCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { SamsungHRSetupGuide } from "./SamsungHRSetupGuide";
import { Capacitor } from "@capacitor/core";

interface BPMStatusDialogProps {
  open: boolean;
  status: 'idle' | 'scanning' | 'connecting' | 'connected' | 'error' | 'error_samsung' | 'error_bluetooth_disabled' | 'error_no_device' | 'healthkit_available';
  onCancel: () => void;
}

const statusConfig = {
  idle: {
    icon: <Heart className="w-6 h-6 text-muted-foreground" />,
    title: "Aguardando",
    description: "Pronto para conectar ao monitor cardíaco"
  },
  scanning: {
    icon: <Loader2 className="w-6 h-6 animate-spin text-primary" />,
    title: "Procurando Monitor Cardíaco...",
    description: "Certifique-se de que seu monitor está ligado e próximo."
  },
  connecting: {
    icon: <Loader2 className="w-6 h-6 animate-spin text-primary" />,
    title: "Conectando...",
    description: "Estabelecendo conexão com seu monitor cardíaco"
  },
  connected: {
    icon: <CheckCircle className="w-6 h-6 text-success" />,
    title: "✓ Conectado!",
    description: "Monitor cardíaco conectado e funcionando"
  },
  healthkit_available: {
    icon: <CheckCircle className="w-6 h-6 text-success" />,
    title: "✓ Apple Health Disponível",
    description: "Detectamos dados de batimento cardíaco do seu relógio via Apple Health. Usando como fonte."
  },
  error_bluetooth_disabled: {
    icon: <AlertCircle className="w-6 h-6 text-destructive" />,
    title: "Bluetooth Desativado",
    description: "Por favor, ative o Bluetooth nas configurações do seu dispositivo para conectar monitores cardíacos."
  },
  error_no_device: {
    icon: <AlertCircle className="w-6 h-6 text-destructive" />,
    title: "Nenhum Monitor Encontrado",
    description: "Certifique-se de que seu monitor cardíaco está ligado, próximo ao celular e pareado via Bluetooth."
  },
  error: {
    icon: <AlertCircle className="w-6 h-6 text-destructive" />,
    title: "Erro na Conexão",
    description: "Não foi possível conectar ao monitor cardíaco. Tente novamente."
  },
  error_samsung: {
    icon: <AlertCircle className="w-6 h-6 text-destructive" />,
    title: "Monitor Não Encontrado",
    description: "Possível dispositivo Samsung detectado. Veja instruções especiais abaixo."
  }
} as const;

export const BPMStatusDialog = ({ open, status, onCancel }: BPMStatusDialogProps) => {
  const [showSamsungGuide, setShowSamsungGuide] = useState(false);
  const platform = Capacitor.getPlatform();
  const hasHealthKit = platform === 'ios';

  // Auto-close após timeout ou erro
  useEffect(() => {
    if (!open) return;

    let timeoutId: NodeJS.Timeout;

    // Auto-close para 'connected' ou 'healthkit_available' após 2 segundos
    if (status === 'connected' || status === 'healthkit_available') {
      timeoutId = setTimeout(() => {
        onCancel();
      }, 2000);
    }

    // Show Samsung guide for error_samsung
    if (status === 'error_samsung') {
      setShowSamsungGuide(true);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [open, status, onCancel]);

  const handleUseHealthKit = async () => {
    setShowSamsungGuide(false);
    onCancel();
    
    // Force HealthKit monitoring
    localStorage.setItem('bpm_prefer_healthkit', 'true');
    
    // Notify parent to restart with HealthKit
    window.dispatchEvent(new CustomEvent('bpm-use-healthkit'));
  };

  // Não renderizar se não estiver aberto
  if (!open) return null;
  
  // Show Samsung guide instead of normal dialog for error_samsung
  if (status === 'error_samsung') {
    return (
      <SamsungHRSetupGuide
        open={showSamsungGuide}
        onClose={() => {
          setShowSamsungGuide(false);
          onCancel();
        }}
        onUseHealthKit={hasHealthKit ? handleUseHealthKit : undefined}
        hasHealthKit={hasHealthKit}
      />
    );
  }
  
  const config = statusConfig[status];
  
  // Proteção contra status inválido
  if (!config) {
    console.warn(`BPMStatusDialog: Invalid status "${status}"`);
    return null;
  }

  return (
    <AlertDialog open={open}>
      <AlertDialogContent className="max-w-[90%] sm:max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-primary/10">
            {config.icon}
          </div>
          <AlertDialogTitle className="text-center">
            {config.title}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-center">
            {config.description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>
            {status === 'error' ? 'Continuar Sem Monitor' : status === 'connected' ? 'OK' : 'Cancelar'}
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
